import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Lock, Globe, Zap, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HowItWorksModal({ open, onOpenChange }: Props) {
  useLanguage();
  const steps = [
    {
      icon: ShieldCheck,
      title: translateInterfaceText("Proteção de Acesso"),
      description: translateInterfaceText("Controle quem pode assistir sua VSL por localização, dispositivo e comportamento.")
    },
    {
      icon: Lock,
      title: translateInterfaceText("Anti-Plágio"),
      description: translateInterfaceText("Dificulte cópias do seu conteúdo e proteja sua propriedade intelectual.")
    },
    {
      icon: Globe,
      title: translateInterfaceText("Redirecionamento Inteligente"),
      description: translateInterfaceText("Envie acessos bloqueados para um vídeo alternativo, URL segura ou mensagem personalizada.")
    },
    {
      icon: Zap,
      title: translateInterfaceText("Filtros Avançados"),
      description: translateInterfaceText("Use UTM, token, chave de URL e bloqueio de VPN/Proxy para reforçar a segurança.")
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn(
          "gap-0 p-0 overflow-hidden border-none shadow-[0_24px_70px_rgba(15,23,42,0.22)] rounded-[24px] bg-white",
          "w-[calc(100vw-24px)] sm:w-[min(680px,calc(100vw-40px))] lg:w-[760px] lg:max-w-[calc(100vw-48px)]",
          "max-h-[calc(100vh-32px)] sm:max-h-[calc(100vh-56px)] lg:max-h-[min(620px,calc(100vh-72px))]"
        )}
      >

        <div className="overflow-y-auto max-h-[inherit]">
          <DialogHeader className="p-[32px_34px_0] text-center sm:text-left border-none">
            <DialogTitle className="text-[24px] sm:text-[28px] font-bold text-[#111827] leading-[1.12] mb-3 tracking-tight">
              {translateInterfaceText("Como funciona a Proteção Anti-Plágio?")}</DialogTitle>
            <p className="text-[14px] sm:text-[15px] text-[#64748B] leading-[1.45] mb-[18px] lg:whitespace-nowrap">
              {translateInterfaceText("Nossa tecnologia de Cloaker protege sua VSL contra cópias de forma simples.")}</p>
            <div className="border-t border-[#E6EBF2] mb-[22px]" />
          </DialogHeader>

          <div className="px-[34px] pb-[28px] grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
            {steps.map((step, idx) => (
              <div 
                key={idx} 
                className="flex flex-col p-[18px] rounded-[18px] border border-[#E5EAF3] bg-white transition-all min-h-[138px]"
              >
                <div className="flex items-center gap-2.5 mb-2.5">
                  <div className="w-9 h-9 shrink-0 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center text-[#2563FF]">
                    <step.icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-[#0F172A] text-[15px] leading-[1.25]">{step.title}</h4>
                </div>
                <p className="text-[13px] text-[#64748B] leading-[1.45]">{step.description}</p>
              </div>
            ))}
          </div>
          
          <div className="p-6 px-10 bg-white border-t border-[#E5EAF3] flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-[13.5px] text-[#64748B] font-medium">{translateInterfaceText("Configure sua primeira proteção em poucos passos.")}</span>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button 
                onClick={() => onOpenChange(false)}
                className="flex-1 sm:flex-initial px-6 h-11 bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#374151] rounded-lg font-bold text-sm transition-all"
              >
                {translateInterfaceText("Entendi")}</button>
              <button 
                onClick={() => {
                  onOpenChange(false);
                  // O modal principal de criação deve ser aberto externamente via callback ou disparando evento
                  const btn = document.querySelector('[data-create-protection-btn]') as HTMLButtonElement;
                  if (btn) btn.click();
                }}
                className="flex-1 sm:flex-initial px-8 h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg font-bold text-sm transition-all shadow-md active:scale-95"
              >
                {translateInterfaceText("Criar Proteção")}</button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

